import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Alert, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { httpsCallable } from "firebase/functions";
import { toast } from 'react-toastify';
import { functions } from "../../services/firebase";
import neoLogo from '../../assets/images/Neo_Logo_A.svg';

const MERCADO_PAGO_SDK_URL = "https://sdk.mercadopago.com/js/v2";
const BRICK_CONTAINER_ID = "methodology-payment-brick-container";

const loadMercadoPagoSdk = () => new Promise((resolve, reject) => {
    if (window.MercadoPago) { resolve(window.MercadoPago); return; }
    const script = document.createElement("script");
    script.src = MERCADO_PAGO_SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => reject(new Error("SDK do Mercado Pago não carregou."));
    document.body.appendChild(script);
});

const processMethodologyPayment = httpsCallable(functions, "processMethodologyPayment");

const MethodologyPaymentModal = ({ isOpen, toggle, idTenant, price, email, name, onPaymentComplete }) => {
    const [brickLoading, setBrickLoading] = useState(false);
    const [brickError, setBrickError] = useState("");
    const [paymentResult, setPaymentResult] = useState(null);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const brickControllerRef = useRef(null);
    const mercadoPagoPublicKey = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY || "";

    const onPaymentCompleteRef = useRef(onPaymentComplete);
    useEffect(() => {
        onPaymentCompleteRef.current = onPaymentComplete;
    }, [onPaymentComplete]);

    useEffect(() => {
        if (!isOpen || paymentResult) return;
        if (!mercadoPagoPublicKey) { setBrickError("Chave pública do Mercado Pago não configurada."); return; }

        let cancelled = false;

        const mountBrick = async () => {
            setBrickLoading(true);
            setBrickError("");
            try {
                await loadMercadoPagoSdk();
                if (cancelled) return;

                if (brickControllerRef.current?.unmount) await brickControllerRef.current.unmount();

                const mp = new window.MercadoPago(mercadoPagoPublicKey, { locale: "pt-BR" });
                const container = document.getElementById(BRICK_CONTAINER_ID);
                if (container) container.innerHTML = "";

                brickControllerRef.current = await mp.bricks().create("payment", BRICK_CONTAINER_ID, {
                    initialization: {
                        amount: Number(price),
                        payer: { email: email || "", entityType: "individual" },
                    },
                    customization: {
                        visual: { 
                            style: { 
                                theme: "default",
                                customVariables: {
                                    baseColor: "#ea580c"
                                }
                            } 
                        },
                        paymentMethods: { creditCard: "all", bankTransfer: "all", ticket: "all", minInstallments: 1, maxInstallments: 1 },
                    },
                    callbacks: {
                        onReady: () => { if (!cancelled) setBrickLoading(false); },
                        onSubmit: async (brickState) => {
                            setBrickLoading(true);
                            setBrickError("");
                            try {
                                const brickFormData = brickState?.formData || brickState || {};
                                const response = await processMethodologyPayment({
                                    idTenant,
                                    email,
                                    name,
                                    formData: brickFormData,
                                    amount: price
                                });

                                const paymentInfo = response.data?.payment || null;
                                setPaymentResult(paymentInfo);

                                if (brickControllerRef.current?.unmount) {
                                    try {
                                        const unmountPromise = brickControllerRef.current.unmount();
                                        if (unmountPromise && typeof unmountPromise.catch === 'function') {
                                            unmountPromise.catch(() => {});
                                        }
                                    } catch (e) { /* ignore */ }
                                    brickControllerRef.current = null;
                                }

                                if (paymentInfo?.status === "approved") {
                                    toast.success("Pagamento aprovado! Metodologia NEO liberada com sucesso.");
                                    if (onPaymentCompleteRef.current) onPaymentCompleteRef.current(paymentInfo);
                                } else if (paymentInfo?.status) {
                                    toast.info("Confira as instruções de pagamento abaixo.");
                                }
                                return response.data;
                            } catch (error) {
                                console.error("Erro pagamento metodologia:", error);
                                setBrickError(error.message || "Erro ao processar pagamento.");
                                throw error;
                            } finally { setBrickLoading(false); }
                        },
                        onError: (error) => {
                            console.error("Brick error:", error);
                            if (!cancelled) { setBrickLoading(false); setBrickError("Erro ao carregar formulário de pagamento."); }
                        },
                    },
                });
            } catch (error) {
                console.error("Erro mount:", error);
                if (!cancelled) { setBrickLoading(false); setBrickError("Erro ao iniciar checkout."); }
            }
        };

        mountBrick();
        return () => {
            cancelled = true;
            try { if (brickControllerRef.current?.unmount) brickControllerRef.current.unmount(); }
            catch (e) { /* ignore */ }
        };
    }, [isOpen, mercadoPagoPublicKey, paymentResult, price, email, name, idTenant]);

    useEffect(() => {
        if (paymentResult?.qr_code_base64 && !brickLoading) {
            setQrModalOpen(true);
        }
    }, [paymentResult, brickLoading]);

    const handleClose = () => {
        toggle();
        setPaymentResult(null);
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="md" centered className="neo-modal">
            <ModalHeader toggle={handleClose} style={{ color: '#ea580c', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                Adquirir Metodologia NEO
            </ModalHeader>
            <ModalBody className="p-4">
                <div className="mb-4 text-center">
                    <h5 style={{ color: '#4a2c11', fontWeight: '900', margin: '0 0 6px 0' }}>Sua Metodologia Pronta</h5>
                    <p style={{ color: '#784b22', fontSize: '0.9rem', margin: 0 }}>
                        {paymentResult ? "Confira as instruções de pagamento." : "Preencha as informações para liberar o planejamento completo."}
                    </p>
                </div>

                {/* Resumo de Valor */}
                <div style={{ background: '#fdfbfb', padding: '15px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <span style={{ color: '#784b22', fontWeight: 'bold', fontSize: '0.85rem', display: 'block', marginBottom: '2px' }}>Valor do Ad-on</span>
                        <span style={{ color: '#ea580c', fontWeight: '900', fontSize: '1.4rem' }}>R$ {price.toFixed(2).replace('.', ',')}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <span style={{ color: '#784b22', fontWeight: 'bold', display: 'block', fontSize: '0.85rem' }}>Beneficiário</span>
                        <span style={{ color: '#4a2c11', fontWeight: 'bold' }}>{name || "Minha Academia"}</span>
                    </div>
                </div>

                {brickError && <Alert color="danger" fade={false}>{brickError}</Alert>}

                <div style={{ minHeight: '300px', display: paymentResult ? 'none' : 'block' }}>
                    {brickLoading && (
                        <div className="text-center py-4">
                            <Spinner color="warning" />
                            <p className="mb-0 mt-3" style={{ color: '#4a2c11', fontWeight: 'bold' }}>Carregando Mercado Pago...</p>
                        </div>
                    )}
                    <div id={BRICK_CONTAINER_ID} />
                </div>

                {paymentResult && (
                    <div style={{ display: 'block' }}>
                        <div className="text-center p-4" style={{ background: '#fdfbfb', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                            <h4 style={{ color: '#4a2c11', fontWeight: '900', marginBottom: '20px' }}>
                                {paymentResult.status === "approved" ? "Acesso Liberado!" : "Conclua o seu Pagamento"}
                            </h4>
                            
                            {paymentResult.status === "approved" && (
                                <p style={{ color: '#ea580c', fontWeight: 'bold' }}>
                                    A Metodologia NEO já foi ativada. Você pode recarregar as Atividades e Níveis para importar todo o planejamento.
                                </p>
                            )}

                            {paymentResult.qr_code_base64 && (
                                <div className="mb-4 text-center">
                                    <p style={{ fontWeight: 'bold', color: '#ea580c', marginBottom: '10px' }}>Copie o Pix abaixo ou leia o QR Code:</p>
                                    <Button 
                                        color="warning" 
                                        outline 
                                        onClick={() => setQrModalOpen(true)}
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        <i className="mdi mdi-qrcode-scan" style={{ marginRight: '5px' }}></i> Visualizar QR Code
                                    </Button>
                                </div>
                            )}

                            {paymentResult.qr_code && (
                                <div>
                                    <div style={{ background: 'rgba(234,88,12,0.05)', padding: '12px', borderRadius: '8px', wordBreak: 'break-all', fontSize: '0.8rem', color: '#4a2c11', border: '1px dashed rgba(234,88,12,0.3)' }}>
                                        {paymentResult.qr_code}
                                    </div>
                                    <Button 
                                        size="sm" 
                                        style={{ marginTop: '10px', background: '#ea580c', border: 'none', fontWeight: 'bold', color: '#fff' }}
                                        onClick={() => {
                                            navigator.clipboard.writeText(paymentResult.qr_code);
                                            toast.success("Código Pix copiado!");
                                        }}
                                    >
                                        <i className="mdi mdi-content-copy" style={{ marginRight: '5px' }} /> Copiar Código
                                    </Button>
                                    <p style={{ fontSize: '0.8rem', color: '#8d9ab9', marginTop: '10px' }}>Assim que o pagamento for identificado, a liberação será instantânea.</p>
                                </div>
                            )}
                            
                            {paymentResult.status !== "approved" && (
                                <div className="mt-4 pt-4" style={{ borderTop: '1px dashed #e2e8f0' }}>
                                    <Button 
                                        size="sm" 
                                        outline
                                        color="secondary"
                                        style={{ fontWeight: 'bold', fontSize: '0.8rem' }}
                                        onClick={() => setPaymentResult(null)}
                                    >
                                        <i className="mdi mdi-refresh" style={{ marginRight: '5px' }} /> Escolher outra forma
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div style={{ borderTop: '1px solid rgba(0,0,0,0.05)', padding: '15px 0 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                    <img src={neoLogo} alt="Neo" style={{ height: '20px', opacity: '0.8' }} />
                    <Button onClick={handleClose} style={{ background: '#e2e8f0', color: '#4a2c11', border: 'none', borderRadius: '8px', padding: '8px 20px', fontWeight: '700' }}>
                        Fechar
                    </Button>
                </div>
            </ModalBody>

            <Modal isOpen={qrModalOpen} toggle={() => setQrModalOpen(!qrModalOpen)} centered>
                <ModalHeader toggle={() => setQrModalOpen(!qrModalOpen)} style={{ color: '#ea580c' }}>
                    QR Code do PIX
                </ModalHeader>
                <ModalBody className="text-center p-4">
                    <p style={{ fontWeight: 'bold', color: '#4a2c11', marginBottom: '20px' }}>Escaneie o QR Code abaixo com seu app bancário:</p>
                    <div style={{ background: '#fff', padding: '16px', display: 'inline-block', borderRadius: '16px', border: '1px dashed #ea580c' }}>
                        <img 
                            src={`data:image/png;base64,${paymentResult?.qr_code_base64}`} 
                            alt="QR Code PIX" 
                            style={{ width: "100%", height: "auto", minHeight: "200px", maxWidth: "250px", display: "block" }} 
                        />
                    </div>
                </ModalBody>
            </Modal>
        </Modal>
    );
};

MethodologyPaymentModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    idTenant: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    email: PropTypes.string,
    name: PropTypes.string,
    onPaymentComplete: PropTypes.func
};

export default MethodologyPaymentModal;
